/**
 * O arquivo pode ser enviado? E, se não pode, POR QUÊ.
 *
 * ## O que isto conserta
 *
 * Até 13/10/2026 o front não conferia nada. O arquivo subia inteiro, o
 * servidor recusava, e aparecia uma frase só:
 *
 *   "Verifique os anexos (tamanho máx. 25MB, tipos permitidos)."
 *
 * Ela não distinguia "grande demais" de "tipo recusado" -- dois problemas com
 * soluções diferentes. Quem tentava mandar uma planilha via a mesma mensagem
 * de quem tentava mandar um vídeo de 2 GB, e nenhum dos dois ficava sabendo o
 * que fazer. No CHAT era pior: o arquivo grande era descartado em SILÊNCIO,
 * sem mensagem nenhuma.
 *
 * ## O limite vem da api, nunca escrito aqui
 *
 * O "25MB" estava chumbado em quatro lugares no operatorweb. No instante em
 * que o limite virou configurável por empresa, os quatro passariam a mentir.
 * Agora o número vem de `GET /politica-de-anexos`, que já devolve o limite
 * EFETIVO -- o menor entre o configurado e o que o servidor web aceita.
 *
 * ## Política ainda não carregada
 *
 * Devolve "pode enviar". O servidor continua sendo a autoridade: deixar passar
 * repete o comportamento de hoje, enquanto bloquear por falta de informação
 * impediria um envio legítimo. A janela é o tempo de uma requisição no
 * carregamento da tela.
 */

/** 1 MB em bytes, na mesma conta que a api usa (KB = 1024). */
const BYTES_POR_MB = 1024 * 1024;

/** A extensão do arquivo, em minúsculas e sem o ponto. */
export const extensaoDe = (nome) => {
    const texto = String(nome || '');
    const ponto = texto.lastIndexOf('.');

    if (ponto <= 0 || ponto === texto.length - 1) return '';

    return texto.slice(ponto + 1).toLowerCase();
};

/** "1,5 MB", "840 KB" -- para a mensagem dizer o tamanho em algo legível. */
export const tamanhoLegivel = (bytes) => {
    const n = Number(bytes);

    if (!isFinite(n) || n < 0) return '';
    if (n < 1024) return `${n} B`;
    if (n < BYTES_POR_MB) return `${Math.round(n / 1024)} KB`;

    const mb = n / BYTES_POR_MB;

    // Abaixo de 10 MB uma casa decimal ajuda ("8,4 MB"); acima dela, não.
    return mb < 10
        ? `${mb.toFixed(1).replace('.', ',')} MB`
        : `${Math.round(mb)} MB`;
};

/**
 * @param {File} arquivo
 * @param {{limiteMb: number, tipos: string[]}|null} politica  `null` = ainda não carregada
 * @returns {{ok: boolean, motivo?: string}}
 */
export const validarAnexo = (arquivo, politica) => {
    if (!arquivo) {
        return { ok: false, motivo: 'Nenhum arquivo selecionado.' };
    }

    if (!politica) {
        return { ok: true };
    }

    const tipos = Array.isArray(politica.tipos) ? politica.tipos : [];
    const extensao = extensaoDe(arquivo.name);

    // O TIPO vem primeiro de propósito: uma planilha de 300 MB tem os dois
    // problemas, e dizer "o tipo não é aceito" é o que resolve o caso dela --
    // reduzir o tamanho não adiantaria nada.
    if (tipos.length > 0 && !tipos.includes(extensao)) {
        const comoFoiEnviado = extensao ? `.${extensao}` : 'sem extensão';

        return {
            ok: false,
            motivo: `Arquivos ${comoFoiEnviado} não são aceitos. Aceitos: ${tipos.join(', ')}.`,
        };
    }

    const limiteMb = Number(politica.limiteMb);

    if (isFinite(limiteMb) && limiteMb > 0 && arquivo.size > limiteMb * BYTES_POR_MB) {
        return {
            ok: false,
            motivo: `Este arquivo tem ${tamanhoLegivel(arquivo.size)} e o limite é ${limiteMb} MB.`,
        };
    }

    return { ok: true };
};

/**
 * O valor do `accept=` do seletor de arquivos, a partir dos mesmos tipos.
 *
 * Existe porque o seletor do chat oferecia `image/png, image/jpeg, .json` --
 * nem PDF aparecia na janela, enquanto o servidor aceitava uma lista bem
 * maior. Montar do mesmo lugar impede que as duas listas voltem a divergir.
 */
export const aceitosNoSeletor = (politica) => {
    const tipos = politica && Array.isArray(politica.tipos) ? politica.tipos : [];

    return tipos.map((t) => `.${t}`).join(',');
};
